import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PanelLoading } from "../../../components/Loading";
import { useToast } from "../../../components/Toast";
import { useAuth } from "../../../auth/AuthContext";
import { localizedName } from "../../../lib/localizedName";
import { formatDateTime } from "../../../lib/format";
import Ic from "../../../components/icons.jsx";
import type { ApiError } from "../../../api/client";
import { qualityReadingApi, type QualityReading } from "../../../api/quality";
import { phaseLabel } from "./QualityPhaseLabels";

/**
 * Red za potpis šefa laboratorije (KK29).
 *
 * Vrednost koja je izašla iz prijemne granice ne važi dok je šef laboratorije ne potpiše. Ekran LOT-a
 * pokazuje samo nalaze LOT-a koji je otvoren, a u njega se ulazi oznakom LOT-a — pa sam po sebi ne može
 * da kaže gde potpis negde čeka. Ovaj spisak to kaže: šta čeka, od kada, i na kom LOT-u.
 *
 * Potpisuje se odavde, jer je potpis radnja nad nalazom a ne nad ekranom. LOT stoji u redu zato što bez
 * njega nalaz nije ništa — ista vrednost na dva LOT-a su dva različita pitanja.
 */
export default function QualityPendingVerificationPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const lang = i18n.resolvedLanguage ?? "sr";
  const smeDaPotpise = hasPermission("quality_readings:verify");

  const [rows, setRows] = useState<QualityReading[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await qualityReadingApi.awaitingVerification());
    } catch (e) {
      toast.error((e as ApiError).message);
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function sign(r: QualityReading) {
    setBusy(r.id);
    try {
      await qualityReadingApi.verify(r.id);
      toast.success(t("pendingVerification.signed", "Potpisano."));
      await load();
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("pendingVerification.title", "Nalazi koji čekaju potpis")}</h1>
          <div className="sub">
            {t(
              "pendingVerification.lead",
              "Vrednost van prijemne granice ne važi dok je šef laboratorije ne potpiše. Ovde su sve takve, najstarija prva.",
            )}
          </div>
        </div>
        <div className="page-actions">
          <button type="button" className="btn" onClick={() => void load()}>
            <Ic.refresh /> {t("common.refresh", "Osveži")}
          </button>
        </div>
      </div>

      {rows === null ? (
        <PanelLoading text={t("pendingVerification.loading", "Čitam red…")} />
      ) : rows.length === 0 ? (
        <div className="empty">{t("pendingVerification.none", "Ništa ne čeka potpis.")}</div>
      ) : (
        <div className="card" style={{ overflow: "hidden", padding: 0 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>{t("pendingVerification.col.subject", "LOT")}</th>
              <th>{t("quality.plan.col.test", "Test")}</th>
              <th>{t("readings.col.value", "Vrednost")}</th>
              <th>{t("pendingVerification.col.phase", "Faza")}</th>
              <th>{t("readings.col.sample", "Uzorak")}</th>
              <th>{t("readings.col.when", "Izmereno")}</th>
              <th>{t("readings.col.who", "Uneo")}</th>
              {smeDaPotpise && <th />}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>
                  {r.goodsLotDesignation ? (
                    <code>{r.goodsLotDesignation}</code>
                  ) : r.productionOrderId ? (
                    <span className="muted">
                      {t("pendingVerification.workOrder", "radni nalog")}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
                <td>{localizedName({ name: r.parameterKey, nameI18n: r.parameterNameI18n }, lang)}</td>
                <td>
                  <strong>{r.displayValue}</strong>
                  {r.unit ? <span className="muted"> {r.unit}</span> : null}
                  {r.outOfRange && (
                    <span className="tag warn">{t("readings.outOfRange", "van granice")}</span>
                  )}
                </td>
                <td className="muted">{phaseLabel(t, r.phase)}</td>
                <td className="muted">
                  {r.palletNo != null ? t("readings.pallet", "paleta {{n}}", { n: r.palletNo }) : ""}
                  {r.sampleLabel ? ` ${r.sampleLabel}` : ""}
                  {r.shiftLabel ? ` · ${r.shiftLabel}` : ""}
                  {r.palletNo == null && !r.sampleLabel && !r.shiftLabel ? "—" : ""}
                </td>
                <td className="muted">{formatDateTime(r.measuredAt)}</td>
                <td className="muted">{r.recordedBy}</td>
                {smeDaPotpise && (
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button
                      type="button"
                      className="btn sm primary"
                      disabled={busy === r.id}
                      onClick={() => void sign(r)}
                    >
                      {t("readings.verify", "Potpiši")}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}
